import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, FreeMode, Mousewheel, Zoom } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import Lenis from 'lenis';
import { Helmet } from 'react-helmet';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

import { INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Header from '../components/Header.js';

import './slider-freemode.scss'
import { getSliderUrl, getFullScreenUrl, isVideo, getVideoUrl } from '../utils/imageLoader.js';

function ArtworksSlider({ artworks, loading = true, buttons = null }) {
    const { slug } = useParams();
    const artwork = artworks.find(art => art.slug === slug);
    const [, setWindowWidth] = useState(window.innerWidth);
    const [imageDimensions, setImageDimensions] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeImage, setActiveImage] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(1);
    const [modalReady, setModalReady] = useState(false);
    const [modalSwiper, setModalSwiper] = useState(null);
    const [modalImagesLoaded, setModalImagesLoaded] = useState({});
    const [imagesLoaded, setImagesLoaded] = useState({});
    const [allImagesReady, setAllImagesReady] = useState(false);

    // Scroll to top on mount
    useEffect(() => {
        const prev = typeof window !== 'undefined' && window.history && 'scrollRestoration' in window.history ? window.history.scrollRestoration : null;
        if (prev !== null) window.history.scrollRestoration = 'manual';
        const scrollTop = () => {
            if (lenisRef.current && typeof lenisRef.current.scrollTo === 'function') {
                lenisRef.current.scrollTo(0, { immediate: true });
            } else {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
            }
        };
        if (typeof requestAnimationFrame !== 'undefined') {
            requestAnimationFrame(() => {
                scrollTop();
                setTimeout(scrollTop, 0);
                setTimeout(scrollTop, 100);
            });
        } else {
            setTimeout(scrollTop, 0);
            setTimeout(scrollTop, 100);
        }
        return () => {
            if (prev !== null) window.history.scrollRestoration = prev;
        };
    }, [slug]);

    /*     useEffect(() => {
            if (artwork) {
                document.title = `${artwork.title} • Alex de Brabant`;
            } else {
                document.title = "Artwork Not Found • Alex de Brabant";
            }
        }, [artwork]);
     */

    // Smooth scroll with Lenis
    const lenisRef = useRef(null);
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        lenisRef.current = lenis;

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
            lenisRef.current = null;
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Add effect to handle overflow
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.overscrollBehaviorX = 'contain';
            document.body.style.touchAction = 'pan-y pinch-zoom';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        }

        return () => {
            document.body.style.overflow = 'auto';
            document.body.style.overscrollBehaviorX = '';
            document.body.style.touchAction = '';
        };
    }, [isModalOpen]);

    // Add effect to handle escape key
    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isModalOpen) {
                setIsModalOpen(false);
            }
        };

        window.addEventListener('keydown', handleEscapeKey);
        return () => window.removeEventListener('keydown', handleEscapeKey);
    }, [isModalOpen]);

    // Navigate to correct slide when modal opens
    useEffect(() => {
        if (isModalOpen && modalSwiper) {
            setModalReady(false);
            // Use requestAnimationFrame to ensure Swiper is fully initialized
            requestAnimationFrame(() => {
                modalSwiper.slideToLoop(activeIndex, 0);
                // Small delay to ensure slide transition completes
                setTimeout(() => {
                    setModalReady(true);
                }, 50);
            });
        } else {
            setModalReady(false);
        }
    }, [isModalOpen, modalSwiper, activeIndex]);

    // Only use slider pictures (mainPicture is just for preview/thumbnail)
    const allImages = artwork ? (artwork.sliderPictures || []).filter(Boolean) : [];

    // Compute when all slider images are ready
    useEffect(() => {
        const totalImages = allImages.length;
        const loadedCount = Object.keys(imagesLoaded).filter(key => imagesLoaded[key]).length;
        if (totalImages > 0 && loadedCount === totalImages) {
            setAllImagesReady(true);
        }
    }, [imagesLoaded, allImages.length]);

    // Reset loading state when artwork/slug changes
    useEffect(() => {
        setImagesLoaded({});
        setAllImagesReady(false);
    }, [slug]);


    // Only show error messages after loading completes
    if (!loading) {
        if (!artwork) {
            return <div>Project not found</div>;
        }

        // Check if artwork has required fields
        if (!artwork.mainPicture && (!artwork.sliderPictures || artwork.sliderPictures.length === 0)) {
            return <div>No images available for this artwork</div>;
        }
    }

    // Return null while loading to prevent flash
    if (loading || !artwork) {
        return null;
    }

    const getImageUrl = (image) => {
        return getSliderUrl(image);
    };

    const handleImageLoad = (event, index) => {
        const img = event.target;
        setImageDimensions(prev => ({
            ...prev,
            [index]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }));
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleFullScreenImageLoad = (event, index) => {
        const img = event.target;
        setImageDimensions(prev => ({
            ...prev,
            [index]: {
                width: img.naturalWidth,
                height: img.naturalHeight
            }
        }));
        setModalImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleVideoLoad = (index) => {
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleModalVideoLoad = (index) => {
        setImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
        setModalImagesLoaded(prev => ({
            ...prev,
            [index]: true
        }));
    };

    const handleImageClick = (index) => {
        setActiveIndex(index);
        setActiveImage(allImages[index]);
        setCurrentSlide(index + 1);
        setIsModalOpen(true);
    };

    const renderOptions = {
        renderText: text =>
            text.split('\n').reduce((children, textSegment, index) => {
                return [...children, index > 0 && <br key={index} />, textSegment];
            }, []),
        renderNode: {
            [INLINES.HYPERLINK]: (node, children) => {
                const { uri } = node.data;
                return (
                    <a href={uri} target="_blank" rel="noopener noreferrer">
                        {children}
                    </a>
                );
            },
        },
    };

    return (
        <AnimatePresence mode="wait">
            <motion.div
                className="commission-project"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Header showSlider={isModalOpen} onCloseSlider={() => setIsModalOpen(false)} pageTitle={artwork?.title || "Artwork"} isArtworkSlider={true} isArtworkModalOpen={isModalOpen} buttons={buttons} />
                <Helmet>
                    <title>{artwork.title}</title>
                    <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <link rel="canonical" href={`https://alexdebrabant.com/${artwork.slug}`} />
                    <meta property="og:title" content={`${artwork.title}`} />
                    <meta property="og:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <meta property="og:type" content="website" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={`${artwork.title}`} />
                    <meta name="twitter:description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                </Helmet>

                {!allImagesReady && (
                    <div className="loading-container">
                        <p className="loading-text">loading</p>
                    </div>
                )}

                <div className="mySwiperContainer" style={{ opacity: allImagesReady ? 1 : 0, transition: 'opacity 0.5s ease-in-out' }}>
                    <Swiper
                        navigation={true}
                        modules={[Navigation, Keyboard, FreeMode, Mousewheel]}
                        loop={true}
                        freeMode={true}
                        className="mySwiper"
                        keyboard={true}
                        slidesPerView="auto"
                        spaceBetween={15}
                        touchRatio={0.7}
                        threshold={5}
                        longSwipesRatio={0.5}
                        preventInteractionOnTransition={true}
                        mousewheel={{
                            enabled: true,
                            forceToAxis: true,
                            sensitivity: 0.4,
                            releaseOnEdges: true,
                            thresholdDelta: 60,
                            thresholdTime: 250
                        }}
                        lazy={{
                            loadPrevNext: true,
                            loadPrevNextAmount: 1,
                        }}
                    >
                        {allImages.map((image, index) => (
                            <SwiperSlide key={index}>
                                {isVideo(image) ? (
                                    <video
                                        src={getVideoUrl(image)}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        onClick={() => handleImageClick(index)}
                                        onLoadedData={() => handleVideoLoad(index)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                ) : (
                                    <img
                                        src={getImageUrl(image)}
                                        alt={artwork.title}
                                        onLoad={(e) => handleImageLoad(e, index)}
                                        width={imageDimensions[index]?.width || 'auto'}
                                        height={imageDimensions[index]?.height || 'auto'}
                                        onClick={() => handleImageClick(index)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>

                {isModalOpen && (
                    <AnimatePresence>
                        <motion.div
                            className={`modal-overlay open ${modalReady ? 'ready' : ''}`}
                            onClick={() => setIsModalOpen(false)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {!modalImagesLoaded[activeIndex] && (
                                <div className="loading-container-modal">
                                    <p className="loading-text">loading</p>
                                </div>
                            )}
                            <div
                                className="modal-content"
                                onClick={e => e.stopPropagation()}
                            >
                                <div className="modal-footer">
                                    <div className="modal-footer-text">
                                        <p className="commission-title">{activeImage?.title || ''}</p>
                                        <p className="commission-second-line">{activeImage?.description || ''}</p>
                                    </div>
                                    <p className="slide-counter">{currentSlide} of {allImages.length}</p>
                                </div>
                                <Swiper
                                    modules={[Navigation, Keyboard, Mousewheel, Zoom]}
                                    navigation={true}
                                    keyboard={true}
                                    initialSlide={activeIndex}
                                    className="modal-swiper"
                                    loop={true}
                                    centeredSlides={true}
                                    onSwiper={setModalSwiper}
                                    onSlideChange={(swiper) => {
                                        setCurrentSlide(swiper.realIndex + 1);
                                        setActiveImage(allImages[swiper.realIndex]);
                                        if (!modalImagesLoaded[swiper.realIndex]) {
                                            setModalImagesLoaded(prev => ({ ...prev, [swiper.realIndex]: false }));
                                        }
                                    }}
                                    zoom={{ toggle: false }}
                                    touchRatio={0.7}
                                    threshold={5}
                                    longSwipesRatio={0.5}
                                    preventInteractionOnTransition={true}

                                    mousewheel={{
                                        enabled: true,
                                        // forceToAxis: true,
                                        sensitivity: 0.4,
                                        releaseOnEdges: true,
                                        thresholdDelta: 60,
                                        //thresholdTime: 250
                                    }}
                                    speed={600}
                                    lazy={{
                                        loadPrevNext: true,
                                        loadPrevNextAmount: 1,
                                    }}
                                >
                                    {allImages.map((image, index) => (
                                        <SwiperSlide key={index}>
                                            {isVideo(image) ? (
                                                <video
                                                    src={getVideoUrl(image)}
                                                    autoPlay
                                                    muted
                                                    loop
                                                    playsInline
                                                    onLoadedData={() => handleModalVideoLoad(index)}
                                                />
                                            ) : (
                                                <div className="swiper-zoom-container">
                                                    <img
                                                        src={getFullScreenUrl(image)}
                                                        alt={artwork.title}
                                                        onLoad={(e) => handleFullScreenImageLoad(e, index)}
                                                        width={imageDimensions[index]?.width || 'auto'}
                                                        height={imageDimensions[index]?.height || 'auto'}
                                                        loading="lazy"
                                                    />
                                                </div>
                                            )}
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                )}

                <div className="commission-text">
                    <p className="commission-text-title">{artwork.textTitle}</p>
                    <div className="commission-text-body"> {artwork.texte && documentToReactComponents(artwork.texte, renderOptions)}</div>

                </div>
            </motion.div>
        </AnimatePresence>
    );
}

export default ArtworksSlider; 